<script setup lang="ts">
import { h, resolveComponent, ref, type Component } from 'vue'
import { useAdmin } from '@/stores/admin'
import type { Organization } from '@/stores/admin'
import type { TableColumn } from '@nuxt/ui'
import { useRouter } from 'vue-router'
import { handleError } from '@/lib/error'
import { formatRelativeTime } from '@/lib/utils'
import { createSortableHeader, createCopyableIdCell } from '@/lib/tableHelpers'

const admin = useAdmin()
const UIcon = resolveComponent('UIcon')
const UAvatar = resolveComponent('UAvatar')
const UButton = resolveComponent('UButton') as Component
const USwitch = resolveComponent('USwitch')

// Create sortable header factory with resolved UButton
const sortable = createSortableHeader(UButton)
const copyableId = createCopyableIdCell(UButton)

const router = useRouter()
const isLoading = ref(admin.organizations.length === 0)
const togglingOrgId = ref<string | null>(null)

// Table sorting state (native UTable/TanStack)
const sorting = ref([{ id: 'name', desc: false }])

async function toggleActive(orgId: string, isActive: boolean) {
  if (togglingOrgId.value) return // Prevent double-click
  togglingOrgId.value = orgId
  try {
    await admin.toggleOrganizationActive(orgId, isActive)
  } catch (error) {
    handleError(error, 'Failed to update organization status', 'Error')
  } finally {
    togglingOrgId.value = null
  }
}
const columns: TableColumn<Organization>[] = [
  {
    accessorKey: 'logo_url',
    header: '',
    size: 50,
    cell: ({ row }) => {
      if (row.getValue('logo_url')) {
        return h(UAvatar, {
          src: row.getValue('logo_url'),
          alt: 'Organization logo',
          width: 24,
          height: 24,
        })
      }
      return h(UIcon, { name: 'i-lucide-circle-dashed' })
    },
  },
  {
    accessorKey: 'name',
    header: sortable('Name'),
    size: 200,
  },
  {
    accessorKey: 'id',
    header: 'ID',
    size: 180,
    cell: ({ row }) => copyableId(row.original.id),
  },
  {
    accessorKey: 'latest_member_login_at',
    header: sortable('Last Activity'),
    size: 120,
    cell: ({ row }) =>
      h('span', { class: 'text-muted' }, formatRelativeTime(row.original.latest_member_login_at)),
  },
  {
    accessorKey: 'is_active',
    header: sortable('Active'),
    size: 80,
    cell: ({ row }) =>
      h(USwitch, {
        modelValue: row.original.is_active,
        disabled: togglingOrgId.value !== null,
        'onUpdate:modelValue': (val: boolean) => toggleActive(row.original.id, val),
      }),
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 160,
    enableHiding: false,
    cell: ({ row }) => {
      return h('div', { class: 'flex gap-1' }, [
        h(
          UButton,
          {
            color: 'warning',
            variant: 'solid',
            size: 'sm',
            icon: 'i-lucide-settings',
            onClick: () =>
              router.push({ name: 'admin-organization', params: { slug: row.original.slug } }),
          },
          () => 'Manage',
        ),
        h(
          UButton,
          {
            to:
              window.location.origin +
              router.resolve({ name: 'admin-preview', params: { orgId: row.original.id } }).href,
            target: '_blank',
            rel: 'noopener noreferrer',
            color: 'neutral',
            variant: 'soft',
            size: 'sm',
            icon: 'i-lucide-eye',
          },
          () => 'Preview',
        ),
      ])
    },
  },
]

function onCreateOrganization() {
  router.push({ name: 'admin-create-organization' })
}

// Refresh data
async function refresh() {
  isLoading.value = true
  try {
    await admin.fetchOrganizations()
  } catch (err) {
    handleError(err, 'Failed to refresh data')
  } finally {
    isLoading.value = false
  }
}

admin.fetchOrganizations().then(() => {
  isLoading.value = false
})
</script>

<template>
  <AppComponent title="Admin home" id="admin-home">
    <template #actions>
      <span class="text-sm text-muted">{{ admin.organizations.length }} organizations</span>
      <UButton
        icon="i-lucide-refresh-cw"
        color="neutral"
        variant="ghost"
        :loading="isLoading"
        @click="refresh"
      />
      <UButton icon="i-lucide-plus" color="primary" @click="onCreateOrganization">
        Create organization
      </UButton>
    </template>

    <div class="p-6">
      <UTable
        :loading="isLoading"
        :data="admin.organizations"
        :columns="columns"
        v-model:sorting="sorting"
      >
        <template #empty>
          <div class="text-center py-6 text-muted">
            {{ isLoading ? 'Loading data...' : 'No data' }}
          </div>
        </template>
      </UTable>
    </div>
  </AppComponent>
</template>
